import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Menu,
  MenuItem,
  CircularProgress,
  Chip,
  Dialog,
  DialogContent,
  Button as MuiButton,
  TextField,
  Stack,
  Alert,
} from "@mui/material";
import {
  MoreVert,
  CheckCircle,
  Cancel,
  Image,
  AddPhotoAlternate,
  Close,
  PhotoCamera,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../../store/Index";
import apiService from "../../../../../service/Api";
import { showErrorToast, showSuccessToast } from "../../../../../components/common/ShowToast";
// ── NEW: import compression utility ──
import { compressImage } from "../../../../../utils/compressImage";
import { getErrorMessage } from "../../../../../utils/getErrorMessage";

interface HandwrittenComment {
  id: number;
  sender_full_name: string;
  sender_email: string;
  content: string;
  handwritten_comment: string | null;
  is_approved: boolean;
  is_spam: boolean;
  created_at: string;
}

interface HandwrittenCommentsProps {
  streamId: string;
  onRefresh?: () => void;
}

const HandwrittenComments: React.FC<HandwrittenCommentsProps> = ({
  streamId,
  onRefresh,
}) => {
  const [comments, setComments] = useState<HandwrittenComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCommentId, setSelectedCommentId] = useState<number | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const reduxTheme = useSelector((state: RootState) => state.theme);

  // Upload modal state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);

  // Fetch comments
  const fetchComments = useCallback(async () => {
    if (!streamId) return;
    setIsLoading(true);
    try {
      const res = await apiService.get<{ results: HandwrittenComment[] }>(
        `/api/chats/memorial-message/?stream_id=${streamId}&comment_source=handwritten`
      );
      setComments(res.data.results || []);
    } catch (error) {
      console.error("Failed to fetch handwritten comments:", error);
      showErrorToast("Failed to load handwritten comments");
    } finally {
      setIsLoading(false);
    }
  }, [streamId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Menu actions
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, commentId: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedCommentId(commentId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCommentId(null);
  };

  const handleApprove = async () => {
    if (!selectedCommentId) return;
    try {
      await apiService.patch(`/api/chats/memorial-message/${selectedCommentId}/`, {
        is_approved: true,
        is_spam: false,
      });
      showSuccessToast("Comment approved");
      fetchComments();
      if (onRefresh) onRefresh();
    } catch {
      showErrorToast("Failed to approve comment");
    }
    handleMenuClose();
  };

  const handleReject = async () => {
    if (!selectedCommentId) return;
    try {
      await apiService.patch(`/api/chats/memorial-message/${selectedCommentId}/`, {
        is_approved: false,
        is_spam: true,
      });
      showSuccessToast("Comment rejected");
      fetchComments();
      if (onRefresh) onRefresh();
    } catch {
      showErrorToast("Failed to reject comment");
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (!selectedCommentId) return;
    try {
      await apiService.delete(`/api/chats/memorial-message/${selectedCommentId}/`);
      showSuccessToast("Comment deleted");
      fetchComments();
      if (onRefresh) onRefresh();
    } catch {
      showErrorToast("Failed to delete comment");
    }
    handleMenuClose();
  };

  // ── Upload logic with compression ──
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setFilePreviewUrl(URL.createObjectURL(file));
    setErrorMsg(null);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      setCameraStream(stream);
      setCameraActive(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch {
      showErrorToast("Could not access camera. Please check permissions.");
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], "captured-photo.webp", { type: "image/webp" });
      setSelectedFile(file);
      setFilePreviewUrl(URL.createObjectURL(file));
      setErrorMsg(null);
      stopCamera();
    }, "image/webp");
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setCameraActive(false);
  };

  // ── MODIFIED: handleUpload with compression ──
  const handleUpload = async () => {
    if (!selectedFile) {
      setErrorMsg("Please select an image.");
      return;
    }
    if (!streamId) {
      setErrorMsg("Stream ID missing.");
      return;
    }
    setUploading(true);
    setErrorMsg(null);
    try {
      // Compress the image before upload
      const compressedFile = await compressImage(selectedFile);
      const formData = new FormData();
      formData.append("stream_id", streamId);
      formData.append("handwritten_comment", compressedFile);
      formData.append("comment_source", "handwritten");
      if (caption.trim()) {
        formData.append("content", caption.trim());
      }
      await apiService.post("/api/chats/memorial-message/", formData);
      showSuccessToast("Handwritten comment uploaded successfully");
      // Reset and close
      setUploadModalOpen(false);
      setSelectedFile(null);
      setFilePreviewUrl(null);
      setCaption("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchComments();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Upload failed:", error);
      const msg = getErrorMessage(error, "Upload failed. Please try again.");
      setErrorMsg(msg);
      showErrorToast(msg);
    } finally {
      setUploading(false);
    }
  };

  const resetUpload = () => {
    setSelectedFile(null);
    setFilePreviewUrl(null);
    setCaption("");
    setErrorMsg(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    stopCamera();
  };

  // ── Render ──
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">Handwritten Comments ({comments.length})</Typography>
        <MuiButton
          variant="contained"
          startIcon={<AddPhotoAlternate />}
          onClick={() => setUploadModalOpen(true)}
          sx={{ bgcolor: reduxTheme.primary_colors[400] }}
        >
          Upload
        </MuiButton>
      </Box>

      {comments.length === 0 ? (
        <Typography color="text.secondary">No handwritten comments found.</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 0 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: reduxTheme.secondary_colors?.[500] || "#f5f5f5" }}>
                <TableCell>Image</TableCell>
                <TableCell>Sender</TableCell>
                <TableCell>Comment</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comments.map((comment) => (
                <TableRow key={comment.id}>
                  <TableCell>
                    {comment.handwritten_comment ? (
                      <Box
                        onClick={() => setPreviewImage(comment.handwritten_comment!)}
                        sx={{ cursor: "pointer", display: "inline-block" }}
                      >
                        <img
                          src={comment.handwritten_comment}
                          alt="Handwritten comment"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Crect width='60' height='60' fill='%23eee'/%3E%3Ctext x='50%25' y='50%25' font-family='sans-serif' font-size='12' fill='%23999' text-anchor='middle' dominant-baseline='central'%3ENo image%3C/text%3E%3C/svg%3E";
                          }}
                          style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 4 }}
                        />
                      </Box>
                    ) : (
                      <Image />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{comment.sender_full_name || "Anonymous"}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {comment.sender_email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{comment.content || "(No text)"}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(comment.created_at).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={comment.is_approved ? "Approved" : "Pending"}
                      color={comment.is_approved ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, comment.id)}>
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleApprove}>
          <CheckCircle fontSize="small" sx={{ mr: 1 }} /> Approve
        </MenuItem>
        <MenuItem onClick={handleReject}>
          <Cancel fontSize="small" sx={{ mr: 1 }} /> Reject (Spam)
        </MenuItem>
        <MenuItem onClick={handleDelete}>Delete</MenuItem>
      </Menu>

      {/* Image Preview Dialog */}
      <Dialog open={!!previewImage} onClose={() => setPreviewImage(null)} maxWidth="md">
        <DialogContent sx={{ p: 2 }}>
          {previewImage && (
            <img
              src={previewImage}
              alt="Full size handwritten comment"
              style={{ width: "100%", height: "auto", maxHeight: "80vh", objectFit: "contain" }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Modal */}
      <Dialog
        open={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          resetUpload();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">Upload Handwritten Comment</Typography>
            <IconButton onClick={() => { setUploadModalOpen(false); resetUpload(); }}>
              <Close />
            </IconButton>
          </Box>

          {errorMsg && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMsg(null)}>
              {errorMsg}
            </Alert>
          )}

          {/* File input */}
          <Box
            sx={{
              border: `2px dashed ${reduxTheme.primary_colors[400]}`,
              borderRadius: 2,
              p: 2,
              textAlign: "center",
              mb: 2,
              cursor: "pointer",
              bgcolor: selectedFile ? reduxTheme.primary_colors[800] : "transparent",
              transition: "0.2s",
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            {selectedFile ? (
              <Box>
                <img
                  src={filePreviewUrl || ""}
                  alt="Preview"
                  style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8 }}
                />
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(0)} KB)
                </Typography>
              </Box>
            ) : (
              <Box>
                <AddPhotoAlternate sx={{ fontSize: 48, color: reduxTheme.primary_colors[400] }} />
                <Typography variant="body2">Click to select an image</Typography>
              </Box>
            )}
          </Box>

          {/* Camera capture */}
          <Box sx={{ mb: 2 }}>
            {!cameraActive ? (
              <MuiButton
                variant="outlined"
                startIcon={<PhotoCamera />}
                onClick={startCamera}
                fullWidth
              >
                Take a photo
              </MuiButton>
            ) : (
              <Box sx={{ position: "relative" }}>
                <video ref={videoRef} style={{ width: "100%", maxHeight: 300, borderRadius: 8 }} />
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <MuiButton variant="contained" onClick={capturePhoto} fullWidth>
                    Capture
                  </MuiButton>
                  <MuiButton variant="outlined" onClick={stopCamera} fullWidth>
                    Cancel
                  </MuiButton>
                </Stack>
              </Box>
            )}
            <canvas ref={canvasRef} style={{ display: "none" }} />
          </Box>

          {/* Caption */}
          <TextField
            label="Caption (optional)"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            fullWidth
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />

          <Stack direction="row" spacing={2}>
            <MuiButton
              variant="outlined"
              onClick={() => { setUploadModalOpen(false); resetUpload(); }}
              fullWidth
            >
              Cancel
            </MuiButton>
            <MuiButton
              variant="contained"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              fullWidth
            >
              {uploading ? <CircularProgress size={24} /> : "Upload"}
            </MuiButton>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default HandwrittenComments;